/* MT QAST AI Photo Try-On: preview-safe storefront bridge. Backend disabled until app activation. */
(() => {
  "use strict";
  const bound = new WeakSet();
  const wait = (ms) => new Promise(r => setTimeout(r, ms));
  const q = (root, selector) => root.querySelector(selector);
  function message(root, msg, error = false) {
    const output = q(root, "[data-tryon-status]");
    if (output) { output.textContent = msg; output.dataset.error = String(error); }
  }
  function init(root) {
    if (bound.has(root)) return;
    bound.add(root);
    const dialog = q(root, "dialog");
    const input = q(root, "[data-tryon-file]");
    const preview = q(root, "[data-tryon-photo-preview]");
    const empty = q(root, "[data-tryon-prompt]");
    const result = q(root, "[data-tryon-result]");
    const placeholder = q(root, "[data-tryon-placeholder]");
    const download = q(root, "[data-tryon-download]");
    const generate = q(root, "[data-tryon-generate]");
    const consent = q(root, "[data-tryon-consent]");
    let live = root.dataset.aiEnabled === "true";
    let captchaSiteKey = "";
    let captchaToken = "";
    let captchaWidgetId = null;
    let captchaLoad = null;
    let imageUrl = null;
    let selected = null;
    let busy = false;
    let cancelled = false;
    function enableButton() {
      generate.disabled = !live || !selected || !consent.checked || busy || !!(captchaSiteKey && !captchaToken);
      if (!live) generate.textContent = "AI generation not enabled";
      else generate.textContent = busy ? "Creating your AI preview…" : "✧ Generate my look";
    }
    function reset() {
      cancelled = true;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      imageUrl = null; selected = null; input.value = "";
      preview.hidden = true; preview.removeAttribute("src");
      empty.hidden = false;
      result.hidden = true; result.removeAttribute("src");
      placeholder.hidden = false; download.hidden = true; download.removeAttribute("href");
      consent.checked = false;
      captchaToken = "";
      if (captchaWidgetId !== null && window.turnstile) window.turnstile.reset(captchaWidgetId);
      message(root, live ? "Add your photo and confirm consent to generate." : "Your photo stays on your device. AI generation is not enabled.");
      enableButton();
    }
    q(root, "[data-tryon-open]")?.addEventListener("click", () => {
      cancelled = false;
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      if (live) setupCaptcha().catch(() => message(root, "Bot check could not load. Please retry.", true));
    });
    function close() { if (typeof dialog.close === "function") dialog.close(); else dialog.removeAttribute("open"); reset(); }
    q(root, "[data-tryon-close]")?.addEventListener("click", close);
    dialog?.addEventListener("click", e => { if (e.target === dialog) close(); });
    dialog?.addEventListener("close", reset);
    q(root, "[data-tryon-clear]")?.addEventListener("click", reset);
    input?.addEventListener("change", () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (!["image/jpeg","image/png","image/webp"].includes(file.type)) { reset(); message(root,"Use JPG, PNG or WebP photos.",true); return; }
      if (file.size > 12 * 1024 * 1024 || file.size === 0) { reset(); message(root,"Choose a photo under 12 MB.",true); return; }
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      selected = file; imageUrl = URL.createObjectURL(file);
      preview.src = imageUrl; preview.hidden = false; empty.hidden = true;
      placeholder.hidden = false; result.hidden = true; download.hidden = true;
      message(root, live ? "Photo ready. Tick the consent box to generate." : "Photo preview ready. AI image generation will be available after secure app setup.");
      enableButton();
    });
    consent?.addEventListener("change", enableButton);
    async function toSafeJpeg(file) {
      const src = URL.createObjectURL(file);
      try {
        const image = await new Promise((resolve, reject) => {
          const img = new Image(); img.onload=()=>resolve(img);img.onerror=()=>reject(Error("Photo could not be opened."));img.src=src;
        });
        if (Math.min(image.naturalWidth, image.naturalHeight) < 250) throw Error("Use a photo at least 250 × 250 pixels.");
        const ratio = Math.min(1, 1536 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.naturalWidth * ratio);
        canvas.height = Math.round(image.naturalHeight * ratio);
        const ctx=canvas.getContext("2d",{alpha:false});
        if(!ctx) throw Error("Cannot prepare photo in this browser.");
        ctx.fillStyle="#fff";ctx.fillRect(0,0,canvas.width,canvas.height);ctx.drawImage(image,0,0,canvas.width,canvas.height);
        const blob=await new Promise(resolve => canvas.toBlob(resolve,"image/jpeg",0.82));
        if(!blob||blob.size>4*1024*1024) throw Error("Could not resize photo; choose a smaller file.");
        return new File([blob],"mt-qast-photo.jpg",{type:"image/jpeg"});
      } finally { URL.revokeObjectURL(src); }
    }
    async function parse(response) {
      let data;
      try { data=await response.json(); }
      catch { throw Error("AI service is not configured. Please try later."); }
      if(!response.ok) throw Error(data.error||"AI service unavailable.");
      return data;
    }
    generate?.addEventListener("click", async () => {
      if(!live||!selected||!consent.checked||busy||(captchaSiteKey&&!captchaToken)) return;
      busy=true;cancelled=false;enableButton();
      try {
        const data=new FormData();
        data.append("photo",await toSafeJpeg(selected));
        data.append("garmentImage",root.dataset.garmentUrl);
        data.append("category",root.dataset.category||"auto");
        data.append("consent","yes");
        if (captchaSiteKey) data.append("cf-turnstile-response", captchaToken);
        message(root,"Submitting your photo securely…");
        const proxy=root.dataset.proxyPath||"/apps/mtq-tryon";
        const started=await parse(await fetch(proxy+"/start",{method:"POST",body:data,credentials:"same-origin",cache:"no-store"}));
        if(!started.token) throw Error("AI job did not start.");
        const deadline=Date.now()+150000;
        while(!cancelled&&Date.now()<deadline) {
          await wait(2500);
          if(cancelled) return;
          const update=await parse(await fetch(proxy+"/status?token="+encodeURIComponent(started.token),{credentials:"same-origin",cache:"no-store"}));
          if(update.status==="completed"&&update.image){
            result.src=update.image;result.hidden=false;placeholder.hidden=true;
            download.href=update.image;download.hidden=false;
            message(root,"Your AI preview is ready. Appearance and fit may differ.");
            return;
          }
          if(update.status==="failed") throw Error(update.error||"Could not generate image.");
          message(root,update.status==="in_queue"?"Your photo is queued…":"AI is creating your preview…");
        }
        if(!cancelled) throw Error("The AI request timed out. Please retry.");
      } catch(e) {
        if(!cancelled) message(root,e.message||"Could not create the preview.",true);
      } finally {busy=false;enableButton();}
    });

    // The Shopify app proxy must answer a signed readiness request. No AI key is ever sent to browsers.
    async function detectService() {
      try {
        const proxy = root.dataset.proxyPath || "/apps/mtq-tryon";
        const response = await fetch(proxy + "/health", { method:"GET", credentials:"same-origin", cache:"no-store" });
        if (!response.ok) return;
        const data = await response.json();
        if (data.ready !== true || data.service !== "mtq-tryon") return;
        live = true;
        captchaSiteKey = typeof data.captchaSiteKey === "string" ? data.captchaSiteKey : "";
        const hint = q(root, ".mtq-tryon__hint");
        if (hint) hint.textContent = "Upload your photo to see an AI-generated garment preview.";
        const resultHint = q(root, "[data-tryon-placeholder]");
        if (resultHint) {
          const title = resultHint.querySelector("strong");
          const caption = resultHint.querySelector("small");
          if (title) title.textContent = "Your AI look";
          if (caption) caption.textContent = "Your generated preview will appear here.";
        }
        message(root, "AI Try-On is connected. Choose a clear photo and confirm permission.");
        enableButton();
        if (dialog && dialog.open) await setupCaptcha();
      } catch {
        // Network, absent app proxy, or non-JSON response: stay in privacy-safe preview mode.
      }
    }
    async function setupCaptcha() {
      if (!captchaSiteKey || captchaWidgetId !== null || !q(root, "[data-tryon-captcha]")) return;
      if (!captchaLoad) {
        captchaLoad = new Promise((resolve,reject)=>{
          if (window.turnstile) { resolve(); return; }
          const script=document.createElement("script");
          script.src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
          script.async=true;
          script.onload=resolve;
          script.onerror=()=>reject(Error("Turnstile could not load."));
          document.head.appendChild(script);
        });
      }
      await captchaLoad;
      if (!window.turnstile || captchaWidgetId !== null) return;
      captchaWidgetId=window.turnstile.render(q(root, "[data-tryon-captcha]"),{
        sitekey: captchaSiteKey,
        callback:token=>{captchaToken=token;enableButton();},
        "expired-callback":()=>{captchaToken="";enableButton();},
        "error-callback":()=>{captchaToken="";enableButton();message(root,"Human verification failed. Try again.",true);}
      });
    }
    detectService();
    enableButton();
  }
  const boot=()=>document.querySelectorAll("[data-mtq-tryon]").forEach(init);
  if(document.readyState==="loading") document.addEventListener("DOMContentLoaded",boot,{once:true}); else boot();
  document.addEventListener("shopify:section:load",boot);
})();